import {
  axi,
  getBackendAssetPath,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {validateData} from '@shared/utils';
import {TranslationDict} from '@shared/types';
import {BoundExternalParticipant, Participant} from '../types';
import {
  participantV8nSchema,
  v8nSchemaOfGetParticipantsResponse,
} from './backendSchemas';
import {GetParticipantsResponse, ParticipantOnBackend} from './backendTypes';
import {mapParticipantFromBackend} from './dataMappers';

export interface GetParticipantsParams extends GetListParams {
  categorySlug?: string;
  subcategorySlug?: string;
  searchQuery?: string;
}

export const getParticipants = async ({
  page,
  pageSize,
  categorySlug,
  subcategorySlug,
  searchQuery,
}: GetParticipantsParams = {}): Promise<GetListOutput<Participant>> => {
  const response = await axi.get<GetParticipantsResponse>(
    'admin/participants',
    {
      params: {
        page,
        per_page: pageSize,
        category_slug: categorySlug || undefined,
        subcategory_slug: subcategorySlug || undefined,
        search_query: searchQuery || undefined,
      },
    },
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetParticipantsResponse,
      'getParticipants',
    );
  } catch {
    //
  }

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items = array?.map(item => mapParticipantFromBackend(item)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const getParticipant = async (id: string): Promise<Participant> => {
  const response = await axi.get<ParticipantOnBackend>(
    `admin/participants/${id}`,
  );

  try {
    validateData(response.data, participantV8nSchema, 'getParticipant');
  } catch {
    //
  }

  return mapParticipantFromBackend(response.data);
};

export const deleteParticipant = async (id: string): Promise<void> => {
  await axi.delete(`admin/participants/${id}`);
};

export interface UpdateParticipantNamesParams {
  participantId: string;
  names: string[];
}

export const updateParticipantNames = async ({
  participantId,
  names,
}: UpdateParticipantNamesParams): Promise<void> => {
  await axi.patch(`admin/participants/${participantId}/names`, {names});
};

export const setDefaultParticipantName = async (
  participantId: string,
  name: string,
): Promise<void> => {
  await axi.patch(`admin/participants/${participantId}/names/default`, {name});
};

export interface CreateParticipantParams {
  categorySlug: string;
  names: string[];
}

export const createParticipant = async ({
  categorySlug,
  names,
}: CreateParticipantParams): Promise<string> => {
  const response = await axi.post<string>('admin/participants', {
    category_slug: categorySlug,
    names,
  });
  return response.data;
};

export interface UpdateParticipantLocalizedNameParams {
  participantId: string;
  localizedName: TranslationDict;
}

export const updateParticipantLocalizedName = async ({
  participantId,
  localizedName,
}: UpdateParticipantLocalizedNameParams): Promise<void> => {
  await axi.patch(`admin/participants/${participantId}/localized-name`, {
    name: localizedName,
  });
};

export const getExternalParticipants = async (
  externalIds: string[],
): Promise<Participant[]> => {
  if (externalIds.length === 0) return [];
  const response = await axi.get<ParticipantOnBackend[]>(
    'admin/participants/external',
    {
      params: {'external_ids[]': externalIds},
      paramsSerializer: {indexes: null},
    },
  );
  return response.data.map(mapParticipantFromBackend);
};

export interface UpsertParticipantInfoParams {
  participantId: string;
  categorySlug: string;
  subcategorySlug?: string;
  colors?: string[] | null;
  abbreviation?: string | null;
  nation: string;
  logoUrl?: string | null;
  logoUrlStroke?: string | null;
  logoUrlPlayer?: string | null;
  logoUrlCustom?: string | null;
}

export const getExternalParticipantUuid = async (
  externalId: string,
  subcategorySlug?: string,
): Promise<string | null> => {
  const response = await axi.get<string | null>(
    'admin/participants/external/uuid',
    {params: {external_id: externalId, subcategory_slug: subcategorySlug}},
  );
  return response.data ?? null;
};

export interface BindExternalParticipantParams {
  participantId: string;
  externalParticipantIds: string[];
}

export const bindExternalParticipant = async ({
  participantId,
  externalParticipantIds,
}: BindExternalParticipantParams): Promise<void> => {
  await axi.post(`admin/participants/${participantId}/external`, {
    external_participant_ids: externalParticipantIds,
  });
};

export const unbindExternalParticipant = async (
  participantId: string,
  externalParticipantId: string,
): Promise<void> => {
  await axi.delete(
    `admin/participants/${participantId}/external/${externalParticipantId}`,
  );
};

export const getBoundExternalParticipants = async (
  participantId: string,
): Promise<BoundExternalParticipant[]> => {
  const response = await axi.get<BoundExternalParticipant[]>(
    `admin/participants/${participantId}/external`,
  );
  return response.data;
};

export const upsertParticipantInfo = async ({
  participantId,
  categorySlug,
  subcategorySlug,
  colors,
  abbreviation,
  nation,
  logoUrl,
  logoUrlStroke,
  logoUrlPlayer,
  logoUrlCustom,
}: UpsertParticipantInfoParams): Promise<void> => {
  await axi.patch('admin/participants', {
    items: [
      {
        participant_id: participantId,
        category_slug: categorySlug,
        subcategory_slug: subcategorySlug || undefined,
        colors: colors ?? [],
        abbreviation: abbreviation ?? null,
        nation,
        logo_url: logoUrl ?? null,
        logo_url_stroke: logoUrlStroke ?? null,
        logo_url_player: logoUrlPlayer ?? null,
        logo_url_custom: logoUrlCustom
          ? getBackendAssetPath(logoUrlCustom)
          : null,
      },
    ],
  });
};
